'use client';

import { Fragment } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CartLaboratoryExam } from './cart-laboratory-exam.component';

import { CartGroupCard } from '@/app/(main)/_components/cart/cart-group-card.component';
import { CartEntityHeader } from '@/app/(main)/_components/cart/cart-item/cart-entity-header.component';
import { CartItemDuplicateWarning } from '@/app/(main)/_components/cart/cart-item/cart-item-duplicate-warning.component';
import { CartItemField } from '@/app/(main)/_components/cart/cart-item/cart-item-field.component';
import { CartItemRemoveButton } from '@/app/(main)/_components/cart/cart-item/cart-item-remove-button.component';
import { CartItemStandaloneField } from '@/app/(main)/_components/cart/cart-item/cart-item-standalone-field.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { CartItem } from '@/types/cart.types';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { formatCartItemScheduleLabel } from '@/utils/cart/format-cart-item-schedule.util';
import { isStandaloneCartItem } from '@/utils/cart/is-standalone-cart-item.util';
import { formatSimpleAddress } from '@/utils/formatters/format-address';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';

interface CartLaboratoryGroupProps {
  items: CartItem[];
  duplicatedItemIds?: ReadonlySet<string>;
  onEditSchedule?: () => void;
  onEditPatient?: () => void;
  onRemoveExam?: (item: CartItem) => void;
  onRemoveAll?: () => void;
}

export function CartLaboratoryGroup({
  items,
  duplicatedItemIds,
  onEditSchedule,
  onEditPatient,
  onRemoveExam,
  onRemoveAll,
}: CartLaboratoryGroupProps) {
  const [firstItem] = items;
  const { partner, patient } = firstItem;

  const hasDuplicatedExam = items.some((item) => duplicatedItemIds?.has(item.id));

  const isStandalone = isStandaloneCartItem(firstItem);
  const schedule = formatCartItemScheduleLabel(firstItem);
  const patientName = formatToTitleCase(patient.socialName || patient.legalName);
  const locationAddress = formatSimpleAddress(partner.facility);

  return (
    <CartGroupCard className="gap-2" dataTestId={DataTestIdEnum.CART_LABORATORY_GROUP}>
      <CartEntityHeader
        name={formatToTitleCase(partner.socialName)}
        avatar={partner.avatar}
        entityType={EntityTypeEnum.LABORATORY_COLLECT_POINT}
        dataTestId={DataTestIdEnum.CART_LABORATORY_GROUP_HEADER}
      />

      {isStandalone && <CartItemStandaloneField />}

      {!isStandalone && schedule && (
        <CartItemField
          label="Data e hora"
          value={<span>{schedule}</span>}
          dataTestId={DataTestIdEnum.CART_ITEM_FIELD_SCHEDULE}
          action={onEditSchedule && {
            label: 'Alterar data e hora',
            dataTestId: DataTestIdEnum.CART_ITEM_EDIT_SCHEDULE_BUTTON,
            onClick: onEditSchedule,
          }}
        />
      )}

      <CartItemField
        label="Atendimento para"
        value={<span>{patientName}</span>}
        dataTestId={DataTestIdEnum.CART_ITEM_FIELD_PATIENT}
        action={onEditPatient && {
          label: 'Alterar paciente',
          dataTestId: DataTestIdEnum.CART_ITEM_EDIT_PATIENT_BUTTON,
          onClick: onEditPatient,
        }}
      />

      <CartItemField
        label="Local"
        value={<span>{locationAddress}</span>}
        dataTestId={DataTestIdEnum.CART_ITEM_FIELD_LOCATION}
      />

      <div className="flex w-full flex-col">
        <Typography className="text-sm font-bold leading-5 text-gray-700">Exames</Typography>

        <div className="flex w-full flex-col gap-2">
          {hasDuplicatedExam && <CartItemDuplicateWarning />}

          {items.map((item) => (
            <Fragment key={item.id}>
              <CartLaboratoryExam
                item={item}
                isDuplicated={duplicatedItemIds?.has(item.id)}
                onRemove={onRemoveExam && (() => onRemoveExam(item))}
              />

              <hr className="w-full border-t border-gray-300" />
            </Fragment>
          ))}
        </div>
      </div>

      {onRemoveAll && (
        <CartItemRemoveButton
          label="Remover todos"
          onRemove={onRemoveAll}
          dataTestId={DataTestIdEnum.CART_LABORATORY_GROUP_REMOVE_ALL_BUTTON}
        />
      )}
    </CartGroupCard>
  );
}
